// src/components/CTASection.jsx
import React from 'react';
import { ChevronRight, Sparkles } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const CTASection = ({ language, t }) => {
  const isRTL = language === 'ar';
  const [ctaRef, ctaVisible] = useScrollAnimation(0.3);

  return (
    <section 
      ref={ctaRef}
      className="py-20 bg-gradient-to-br from-[#2beaf4]/10 via-white to-[#582f8f]/10 relative overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-32 h-32 bg-[#2beaf4]/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute top-20 right-20 w-48 h-48 bg-[#582f8f]/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-10 left-1/3 w-24 h-24 bg-[#1b0976]/15 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Floating sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <Sparkles
            key={i}
            className={`absolute opacity-30 animate-pulse ${
              i % 2 === 0 ? 'animate-float text-[#2beaf4]' : 'animate-bounce text-[#582f8f]'
            }`}
            style={{
              left: `${10 + (i * 15)}%`,
              top: `${20 + (i * 10)}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + (i * 0.5)}s`
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center transition-all duration-1000 ease-out ${
          ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Main headline */}
          <div className={`mb-6 transition-all duration-1200 delay-200 ease-out ${
            ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1b0976] mb-4">
              <span className="bg-gradient-to-r from-[#2beaf4] to-[#582f8f] bg-clip-text text-transparent">
                {t.cta.title}
              </span>
            </h2>
          </div>

          {/* Subtitle */}
          <div className={`mb-8 transition-all duration-1200 delay-400 ease-out ${
            ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            <p className="text-xl text-[#1b0976]/70 max-w-2xl mx-auto">
              {t.cta.subtitle}
            </p>
          </div>

          {/* CTA Button */}
          <div className={`transition-all duration-1200 delay-600 ease-out ${
            ctaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'
          }`}>
            <button className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#2beaf4] to-[#582f8f] text-white rounded-lg hover:from-[#582f8f] hover:to-[#1b0976] transition-all duration-300 font-medium text-lg shadow-lg hover:shadow-2xl transform hover:-translate-y-1 hover:scale-105 overflow-hidden">
              {/* Button glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#2beaf4] to-[#582f8f] opacity-0 group-hover:opacity-75 transition-opacity duration-300 blur-xl"></div>
              
              {/* Button content */}
              <span className="relative z-10 flex items-center">
                {t.cta.button}
                <ChevronRight className={`w-5 h-5 transition-all duration-300 group-hover:translate-x-1 group-hover:scale-110 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              </span>
              
              {/* Shimmer effect */}
              <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20 group-hover:animate-shimmer"></div>
            </button>
          </div>

          {/* Trust indicators */}
          <div className={`mt-12 transition-all duration-1200 delay-800 ease-out ${
            ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <div className="flex flex-wrap justify-center items-center gap-6 lg:gap-8 text-sm text-[#1b0976]/60">
              {/* <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#2beaf4] rounded-full animate-pulse"></div>
                <span>Trusted by 2M+ users</span>
              </div> */}
              {/* <div className="hidden sm:block w-px h-4 bg-[#1b0976]/30"></div> */}
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#582f8f] rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                <span>Bank-level security</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-[#1b0976]/30"></div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#1b0976] rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                <span>5-minute setup</span>
              </div>
            </div>
          </div>

          {/* Additional visual elements */}
          <div className={`mt-16 transition-all duration-1200 delay-1000 ease-out ${
            ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}>
            <div className="flex justify-center space-x-4">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full animate-pulse ${
                    i === 0 ? 'bg-gradient-to-r from-[#2beaf4] to-[#582f8f]' :
                    i === 1 ? 'bg-gradient-to-r from-[#582f8f] to-[#1b0976]' :
                    'bg-gradient-to-r from-[#1b0976] to-[#2beaf4]'
                  }`}
                  style={{ animationDelay: `${i * 0.3}s` }}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;